import { now, parseTime } from "@internationalized/date";

export function getDefaultTimeZone() {
  return "America/Los_Angeles";
}

/**
 * Returns the current time in the default timezone as an "HH:MM:SS" string,
 * offset by the given number of milliseconds.
 */
export function formatTimeString(offsetMs = 0): string {
  const tz = getDefaultTimeZone();
  const current = now(tz).add({ milliseconds: offsetMs });
  const h = String(current.hour).padStart(2, "0");
  const m = String(current.minute).padStart(2, "0");
  const s = String(current.second).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

/**
 * Formats an "HH:MM:SS" or "HH:MM" time string into a human-readable
 * 12-hour format like "2:30 PM".
 */
export function formatScheduledTime(raw: string): string {
  const time = parseTime(raw);
  const period = time.hour >= 12 ? "PM" : "AM";
  const displayHours =
    time.hour === 0 ? 12 : time.hour > 12 ? time.hour - 12 : time.hour;
  const minutes = String(time.minute).padStart(2, "0");
  return `${displayHours}:${minutes} ${period}`;
}

/**
 * Returns Tailwind color classes for a scheduled time based on how it
 * compares to the current time in the default timezone.
 *
 * - Past: red
 * - Within 2 minutes: yellow
 * - Future / no time: gray
 */
export function getScheduledStatusColors(scheduledTime: string | null): {
  bg: string;
  text: string;
} {
  if (!scheduledTime) return { bg: "bg-gray-300", text: "text-gray-400" };
  const tz = getDefaultTimeZone();
  const current = now(tz);
  const scheduled = parseTime(scheduledTime);
  const nowTime = parseTime(
    `${String(current.hour).padStart(2, "0")}:${String(current.minute).padStart(2, "0")}:${String(current.second).padStart(2, "0")}`,
  );
  const diff = scheduled.compare(nowTime);
  if (diff < 0) return { bg: "bg-red-500", text: "text-red-500" };
  if (diff <= 2 * 60 * 1000)
    return { bg: "bg-yellow-500", text: "text-yellow-600" };
  return { bg: "bg-gray-300", text: "text-gray-400" };
}
